import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Grid,
    CircularProgress,
    Alert,
    TextField,
    InputAdornment,
    alpha,
    useTheme
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import StudentLayout from 'components/layout/StudentLayout';
import QuizCardV2 from 'components/quiz/QuizCardV2';
import { getPublishedQuizzes, getMyAttempt } from 'services/quizService';
import { QuizSummary, QuizAttempt } from 'types/quiz';

interface QuizWithAttempt extends QuizSummary {
    attempt?: QuizAttempt | null;
}

function QuizzesPageV2() {
    const theme = useTheme();
    const navigate = useNavigate();
    const [quizzes, setQuizzes] = useState<QuizWithAttempt[]>([]);
    const [filteredQuizzes, setFilteredQuizzes] = useState<QuizWithAttempt[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState<string>('');

    useEffect(() => {
        fetchQuizzes();
    }, []);

    useEffect(() => {
        if (searchQuery.trim() === '') {
            setFilteredQuizzes(quizzes);
        } else {
            const query = searchQuery.toLowerCase();
            const filtered = quizzes.filter(
                (quiz) =>
                    quiz.title.toLowerCase().includes(query) ||
                    (quiz.description && quiz.description.toLowerCase().includes(query))
            );
            setFilteredQuizzes(filtered);
        }
    }, [searchQuery, quizzes]);

    const fetchQuizzes = async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await getPublishedQuizzes(1, 100);

            const quizzesWithAttempts = await Promise.all(
                response.quizzes.map(async (quiz) => {
                    try {
                        const attempt = await getMyAttempt(quiz.id);
                        return { ...quiz, attempt };
                    } catch (err) {
                        return { ...quiz, attempt: null };
                    }
                })
            );

            setQuizzes(quizzesWithAttempts);
            setFilteredQuizzes(quizzesWithAttempts);
        } catch (err: unknown) {
            console.error('Error fetching quizzes:', err);
            const error = err as { response?: { data?: { error?: string } } };
            setError(error.response?.data?.error || 'Failed to load quizzes. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleStartQuiz = (quizId: number) => {
        navigate(`/student/v2/quizzes/${quizId}/play`);
    };

    const handleViewResults = (quizId: number) => {
        navigate(`/student/v2/quizzes/${quizId}/results`);
    };

    return (
        <StudentLayout>
            <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
                {/* Header */}
                <Box
                    sx={{
                        mb: 4,
                        p: 4,
                        borderRadius: 4,
                        background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.1)} 100%)`,
                        border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`
                    }}
                >
                    <Typography
                        variant="h3"
                        fontWeight="800"
                        sx={{
                            background: `linear-gradient(135deg, ${theme.palette.secondary.main}, ${theme.palette.primary.main})`,
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            mb: 1
                        }}
                    >
                        Interactive Quizzes
                    </Typography>
                    <Typography variant="h6" color="text.secondary" fontWeight="400">
                        Test your knowledge and track your progress
                    </Typography>
                </Box>

                {/* Search Bar */}
                <Box sx={{ mb: 4 }}>
                    <TextField
                        fullWidth
                        variant="outlined"
                        placeholder="Search quizzes by title or description..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 3,
                                bgcolor: 'background.paper'
                            }
                        }}
                    />
                </Box>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                        <CircularProgress />
                    </Box>
                ) : error ? (
                    <Alert severity="error" sx={{ mb: 3, borderRadius: 3 }}>
                        {error}
                    </Alert>
                ) : filteredQuizzes.length === 0 ? (
                    <Box
                        sx={{
                            p: 6,
                            textAlign: 'center',
                            borderRadius: 3,
                            border: `1px solid ${theme.palette.divider}`,
                            bgcolor: 'background.paper'
                        }}
                    >
                        <Typography variant="h5" color="text.secondary" fontWeight="600" gutterBottom>
                            {searchQuery ? 'No quizzes found' : 'No quizzes available'}
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            {searchQuery ? 'Try adjusting your search terms.' : 'Check back later for new quizzes!'}
                        </Typography>
                    </Box>
                ) : (
                    <Grid container spacing={3}>
                        {filteredQuizzes.map((quiz) => (
                            <Grid item xs={12} sm={6} lg={4} key={quiz.id}>
                                <QuizCardV2
                                    quiz={quiz}
                                    attempt={quiz.attempt}
                                    onStart={() => handleStartQuiz(quiz.id)}
                                    onViewResults={() => handleViewResults(quiz.id)}
                                />
                            </Grid>
                        ))}
                    </Grid>
                )}
            </Box>
        </StudentLayout>
    );
}

export default QuizzesPageV2;
